import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  adminLogin,
  adminListOrders,
  adminListReviews,
  adminUpdateOrder,
  adminApproveReview,
  adminDeleteReview,
  setAuthToken,
} from "@/lib/api";

const TOKEN_KEY = "crvd_admin_token";

const StatusBadge = ({ status }) => {
  const map = {
    pending: "bg-[#333] text-bone",
    confirmed: "bg-wood text-ink",
    shipped: "bg-blue-900/60 text-blue-200",
    delivered: "bg-green-900/60 text-green-200",
    cancelled: "bg-danger/30 text-danger",
  };
  return (
    <span className={`font-mono text-[10px] tracking-tech px-2 py-1 ${map[status] || map.pending}`}>
      {status.toUpperCase()}
    </span>
  );
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [orders, setOrders] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [tab, setTab] = useState("orders");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (token) {
      setAuthToken(token);
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const load = async () => {
    setLoading(true);
    try {
      const [o, r] = await Promise.all([adminListOrders(), adminListReviews()]);
      setOrders(o);
      setReviews(r);
    } catch (e) {
      if (e?.response?.status === 401) logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (e) => {
    e.preventDefault();
    try {
      const res = await adminLogin(email, password);
      localStorage.setItem(TOKEN_KEY, res.token);
      setToken(res.token);
      toast.success("Logged in");
    } catch {
      toast.error("Invalid credentials");
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthToken(null);
    setToken(null);
  };

  const changeStatus = async (id, status) => {
    try {
      await adminUpdateOrder(id, status);
      setOrders((os) => os.map((o) => (o.id === id ? { ...o, status } : o)));
      toast.success("Order updated");
    } catch {
      toast.error("Update failed");
    }
  };

  const toggleReview = async (id, approved) => {
    try {
      await adminApproveReview(id, approved);
      setReviews((rs) => rs.map((r) => (r.id === id ? { ...r, approved } : r)));
      toast.success(approved ? "Approved" : "Unpublished");
    } catch {
      toast.error("Failed");
    }
  };

  const removeReview = async (id) => {
    if (!window.confirm("Delete review?")) return;
    try {
      await adminDeleteReview(id);
      setReviews((rs) => rs.filter((r) => r.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Failed");
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center p-6">
        <form
          onSubmit={login}
          className="border border-[#333] p-8 max-w-md w-full"
          data-testid="admin-login-form"
        >
          <div className="font-mono text-[11px] tracking-tech text-wood mb-4">
            // CRVD ADMIN
          </div>
          <h1 className="font-display text-4xl tracking-brutal uppercase mb-8">
            CONTROL ROOM
          </h1>
          <div className="space-y-4">
            <Input
              required
              type="email"
              placeholder="admin@runcrvd.com"
              data-testid="admin-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-steel border-[#333] rounded-none font-mono"
            />
            <Input
              required
              type="password"
              placeholder="password"
              data-testid="admin-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-steel border-[#333] rounded-none font-mono"
            />
            <button
              type="submit"
              data-testid="admin-login-submit"
              className="w-full bg-wood text-ink font-mono text-xs tracking-tech py-4 hover:bg-bone transition-colors"
            >
              ENTER
            </button>
          </div>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="mt-6 font-mono text-[10px] tracking-tech text-bone/50 hover:text-wood"
          >
            ← back to site
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink text-bone">
      <header className="border-b border-[#222] px-6 md:px-10 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="inline-block w-3 h-3 bg-wood" />
          <span className="font-display text-2xl tracking-brutal">CRVD / ADMIN</span>
        </div>
        <div className="flex items-center gap-4">
          <Link to="/" className="font-mono text-[11px] tracking-tech text-bone/60 hover:text-wood">
            ↗ site
          </Link>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="font-mono text-[11px] tracking-tech border border-[#333] px-3 py-1.5 hover:border-danger hover:text-danger"
          >
            LOGOUT
          </button>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-l border-t border-[#222] mb-10">
          <KPI label="ORDERS" value={orders.length} />
          <KPI label="PENDING" value={orders.filter((o) => o.status === "pending").length} />
          <KPI label="REVIEWS" value={reviews.length} />
          <KPI label="AWAITING APPROVAL" value={reviews.filter((r) => !r.approved).length} />
        </div>

        {/* Tabs */}
        <div className="flex border-b border-[#222] mb-6">
          {[
            { id: "orders", label: "ORDERS" },
            { id: "reviews", label: "REVIEWS" },
          ].map((tb) => (
            <button
              key={tb.id}
              onClick={() => setTab(tb.id)}
              data-testid={`admin-tab-${tb.id}`}
              className={`font-mono text-xs tracking-tech px-6 py-3 border-b-2 ${
                tab === tb.id
                  ? "border-wood text-wood"
                  : "border-transparent text-bone/50 hover:text-bone"
              }`}
            >
              {tb.label}
            </button>
          ))}
        </div>

        {loading && (
          <div className="font-mono text-xs text-bone/50 py-6">LOADING…</div>
        )}

        {tab === "orders" && (
          <div className="overflow-x-auto" data-testid="admin-orders-list">
            <table className="w-full border border-[#222] text-sm">
              <thead className="bg-steel font-mono text-[10px] tracking-tech text-bone/60">
                <tr>
                  <th className="p-3 text-left">ORDER#</th>
                  <th className="p-3 text-left">CUSTOMER</th>
                  <th className="p-3 text-left">CAR</th>
                  <th className="p-3 text-left">QTY</th>
                  <th className="p-3 text-left">TOTAL</th>
                  <th className="p-3 text-left">STATUS</th>
                  <th className="p-3 text-left">ACTIONS</th>
                </tr>
              </thead>
              <tbody className="font-mono text-xs">
                {orders.length === 0 && (
                  <tr>
                    <td colSpan={7} className="p-6 text-center text-bone/40">
                      NO ORDERS YET
                    </td>
                  </tr>
                )}
                {orders.map((o) => (
                  <tr key={o.id} className="border-t border-[#222] hover:bg-steel/40">
                    <td className="p-3 text-wood">{o.order_number}</td>
                    <td className="p-3">
                      <div>{o.customer_name}</div>
                      <div className="text-bone/50 text-[10px]">{o.email}</div>
                    </td>
                    <td className="p-3">{o.car_model}</td>
                    <td className="p-3">{o.quantity}</td>
                    <td className="p-3">€{o.total}</td>
                    <td className="p-3"><StatusBadge status={o.status} /></td>
                    <td className="p-3">
                      <select
                        value={o.status}
                        onChange={(e) => changeStatus(o.id, e.target.value)}
                        data-testid={`order-status-${o.id}`}
                        className="bg-steel border border-[#333] font-mono text-[10px] px-2 py-1 text-bone"
                      >
                        {["pending", "confirmed", "shipped", "delivered", "cancelled"].map((s) => (
                          <option key={s} value={s}>{s.toUpperCase()}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "reviews" && (
          <div className="grid md:grid-cols-2 gap-0 border-l border-t border-[#222]" data-testid="admin-reviews-list">
            {reviews.length === 0 && (
              <div className="col-span-full p-6 font-mono text-xs text-bone/40 text-center border-r border-b border-[#222]">
                NO REVIEWS
              </div>
            )}
            {reviews.map((r) => (
              <div key={r.id} className="border-r border-b border-[#222] p-5 bg-steel/10">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="font-mono text-xs text-wood">{r.customer_name}</div>
                    <div className="font-mono text-[10px] text-bone/50">{r.car_model}</div>
                  </div>
                  <span
                    className={`font-mono text-[9px] tracking-tech px-2 py-0.5 ${
                      r.approved ? "bg-wood text-ink" : "bg-[#333] text-bone/70"
                    }`}
                  >
                    {r.approved ? "LIVE" : "PENDING"}
                  </span>
                </div>
                <div className="font-display text-lg uppercase tracking-brutal mb-2">{r.title}</div>
                <div className="font-sans text-sm text-bone/70 mb-3">{r.body}</div>
                <div className="font-mono text-[10px] text-wood mb-4">
                  {"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => toggleReview(r.id, !r.approved)}
                    data-testid={`review-toggle-${r.id}`}
                    className="font-mono text-[10px] tracking-tech border border-wood text-wood px-3 py-1.5 hover:bg-wood hover:text-ink"
                  >
                    {r.approved ? "UNPUBLISH" : "APPROVE"}
                  </button>
                  <button
                    onClick={() => removeReview(r.id)}
                    data-testid={`review-delete-${r.id}`}
                    className="font-mono text-[10px] tracking-tech border border-danger text-danger px-3 py-1.5 hover:bg-danger hover:text-ink"
                  >
                    DELETE
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const KPI = ({ label, value }) => (
  <div className="border-r border-b border-[#222] p-5">
    <div className="font-mono text-[10px] tracking-tech text-bone/50">{label}</div>
    <div className="font-display text-4xl text-wood mt-1">{value}</div>
  </div>
);
